/**
 * Geometría de presentación de estímulos.
 *
 * Todo se expresa en **píxeles CSS del viewport** (el mismo sistema que entrega WebGazer y
 * `getBoundingClientRect()`). `devicePixelRatio` se guarda solo como metadato: nunca se
 * multiplica ninguna coordenada por él.
 */

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ImageBounds {
  viewportWidth: number;
  viewportHeight: number;
  displayedLeft: number;
  displayedTop: number;
  displayedWidth: number;
  displayedHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  devicePixelRatio: number;
}

export interface NormalizedPoint {
  xNorm: number;
  yNorm: number;
  onImage: boolean;
}

/**
 * Rectángulo realmente pintado de una imagen con `object-fit: contain` (centrada) dentro de un
 * contenedor. Devuelve las bandas vacías implícitas como diferencia con el contenedor.
 */
export function computeContainRect(container: Rect, naturalWidth: number, naturalHeight: number): Rect {
  if (!(naturalWidth > 0) || !(naturalHeight > 0) || !(container.width > 0) || !(container.height > 0)) {
    throw new Error("Dimensiones inválidas para calcular object-fit: contain");
  }
  const scale = Math.min(container.width / naturalWidth, container.height / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return {
    left: container.left + (container.width - width) / 2,
    top: container.top + (container.height - height) / 2,
    width,
    height,
  };
}

/** Márgenes (bandas vacías) entre el contenedor y la imagen pintada. */
export function containMargins(container: Rect, painted: Rect) {
  return {
    left: painted.left - container.left,
    right: container.left + container.width - (painted.left + painted.width),
    top: painted.top - container.top,
    bottom: container.top + container.height - (painted.top + painted.height),
  };
}

/**
 * Proyecta una coordenada de pantalla (px CSS) al sistema normalizado de la imagen [0,1]².
 * No aplica `clamp`: un punto en el margen queda fuera de [0,1] y `onImage=false`.
 * Los bordes exactos 0 y 1 se consideran dentro de la imagen.
 */
export function screenToImageNorm(xScreen: number, yScreen: number, bounds: ImageBounds): NormalizedPoint {
  const xNorm = (xScreen - bounds.displayedLeft) / bounds.displayedWidth;
  const yNorm = (yScreen - bounds.displayedTop) / bounds.displayedHeight;
  const onImage = xNorm >= 0 && xNorm <= 1 && yNorm >= 0 && yNorm <= 1;
  return { xNorm, yNorm, onImage };
}

/** Inversa de `screenToImageNorm` (útil para simulación y pruebas). */
export function imageNormToScreen(xNorm: number, yNorm: number, bounds: ImageBounds): { x: number; y: number } {
  return {
    x: bounds.displayedLeft + xNorm * bounds.displayedWidth,
    y: bounds.displayedTop + yNorm * bounds.displayedHeight,
  };
}

/**
 * Construye `ImageBounds` a partir del rectángulo medido del elemento `<img>` (que puede incluir
 * bandas si usa `object-fit: contain` y ocupa todo el contenedor). Si el elemento ya tiene el
 * tamaño exacto del contenido (sin bandas), el resultado coincide con el rectángulo medido.
 */
export function boundsFromElementRect(
  elementRect: Rect,
  naturalWidth: number,
  naturalHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  devicePixelRatio: number,
): ImageBounds {
  const painted = computeContainRect(elementRect, naturalWidth, naturalHeight);
  return {
    viewportWidth,
    viewportHeight,
    displayedLeft: painted.left,
    displayedTop: painted.top,
    displayedWidth: painted.width,
    displayedHeight: painted.height,
    naturalWidth,
    naturalHeight,
    devicePixelRatio,
  };
}

/** Indica si dos geometrías son equivalentes (tolerancia sub-píxel). Útil para detectar cambios a mitad de ensayo. */
export function sameGeometry(a: ImageBounds, b: ImageBounds, tolerancePx = 0.5): boolean {
  return (
    Math.abs(a.viewportWidth - b.viewportWidth) <= tolerancePx &&
    Math.abs(a.viewportHeight - b.viewportHeight) <= tolerancePx &&
    Math.abs(a.displayedLeft - b.displayedLeft) <= tolerancePx &&
    Math.abs(a.displayedTop - b.displayedTop) <= tolerancePx &&
    Math.abs(a.displayedWidth - b.displayedWidth) <= tolerancePx &&
    Math.abs(a.displayedHeight - b.displayedHeight) <= tolerancePx &&
    a.devicePixelRatio === b.devicePixelRatio
  );
}
